import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import Badge from "../../../../components/bootstrap/Badge";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import { MoreActions } from "../../../../components/extras/MoreActions";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import PaginationButtons, { dataPagination, PER_COUNT } from "../../../../components/PaginationButtons";
import { hiddenMenu } from "../../../../menu";
import surveyService from "../../../../services/survey-service";
import LoadingPage from "../../loading/LoadingPage";
import { SurveyModal } from "./SurveyModal";

export const CommonSurveyList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_COUNT['10']);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);

    const [surveys, setSurveys] = useState<ISurvey[]>([]);
    const [selectedSurvey, setSelectedSurvey] = useState<ISurvey | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchSurveys();
    }, []);

    const fetchSurveys = async () => {
        setIsLoading(true);

        const response = await surveyService.getSurveys();

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        setSurveys(response.surveys || []);
    };

    const toggleModal = (isVisible: boolean) => {
        setIsModalOpened(isVisible);
    };

    const handleAddClick = () => {
        toggleModal(true);
    };

    const onModalClose = () => {
        toggleModal(false);
        setSelectedSurvey(null);
    };

    const onAfterSubmit = async () => {
        await fetchSurveys();
        onModalClose();
    };

    const handleDeleteClick = async (survey: ISurvey) => {
        if (!survey.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await surveyService.deleteSurvey(survey.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchSurveys();
    };

    const handleEditClick = (survey: ISurvey) => {
        setSelectedSurvey(survey);
        toggleModal(true);
    };

    const handleSettingsClick = async (survey: ISurvey) => {
        if (survey.id)
            navigate(`../${hiddenMenu.surveySettings.path.replace(':id', survey.id)}`);
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <Card style={{ minHeight: 600 }}>
                <CardHeader borderSize={1}>
                    <CardLabel icon="Feed" iconColor="primary">
                        <CardTitle tag="div" className="h5">
                            Questionários
                        </CardTitle>
                    </CardLabel>
                    <CardActions>
                        <Button color="light" icon="AddCircle" onClick={handleAddClick}>
                            Adicionar
                        </Button>
                    </CardActions>
                </CardHeader>
                <CardBody className="table-responsive">
                    <table className="table table-modern table-hover">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Situação</th>
                                <td aria-labelledby="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {dataPagination(surveys, currentPage, perPage).map((item: ISurvey) => (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>
                                        {item.status ? (
                                            <h5>
                                                <Badge color="success" className="align-items-center justify-content-center">
                                                    Ativo
                                                </Badge>
                                            </h5>
                                        ) : (
                                            <h5>
                                                <Badge color="danger" className="align-items-center justify-content-center">
                                                    Inativo
                                                </Badge>
                                            </h5>
                                        )}
                                    </td>

                                    <MoreActions
                                        id={item.id}
                                        showEdit={true}
                                        showDelete={true}
                                        onEditClick={() => handleEditClick(item)}
                                        onDeleteClick={() => handleDeleteClick(item)}
                                        customActions={[
                                            {
                                                label: 'Manutenção',
                                                icon: 'Settings',
                                                onClickAction: () => handleSettingsClick(item),
                                            }
                                        ]}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardBody>
                <PaginationButtons data={surveys} label="items" setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />
            </Card>

            <SurveyModal isModalOpened={isModalOpened} onModalClose={onModalClose} onAfterSubmit={onAfterSubmit} selectedSurvey={selectedSurvey} />
        </>
    );
};
